import { apiDelete, apiGet, apiPost, apiPut, queryString, type ApiResult } from "./client";
import type { Candidates, ChangeLogEntry, Lesson, LessonKey } from "./types";
import type { CellRange } from "../time";

/** "/lessons/{groupId}/{date}/{slotId}" (SPEC §8.5). */
export function lessonPath(key: LessonKey): string {
  return `/lessons/${key.groupId}/${key.date}/${key.slotId}`;
}

export async function fetchLesson(key: LessonKey): Promise<ApiResult<Lesson>> {
  return apiGet<Lesson>(lessonPath(key));
}

export async function fetchCandidates(key: LessonKey): Promise<ApiResult<Candidates>> {
  return apiGet<Candidates>(lessonPath(key) + "/candidates" + queryString({ includeExcluded: true }));
}

export async function fetchLessonHistory(key: LessonKey): Promise<ApiResult<ChangeLogEntry[]>> {
  return apiGet<ChangeLogEntry[]>(lessonPath(key) + "/history");
}

export async function cancelLesson(key: LessonKey, version: number): Promise<ApiResult<Lesson>> {
  return apiPost<Lesson>(lessonPath(key) + "/cancel", { version });
}

export async function restoreLesson(key: LessonKey, version: number, confirm: boolean): Promise<ApiResult<Lesson>> {
  return apiPost<Lesson>(lessonPath(key) + "/restore", { version, confirm });
}

export async function assignSubstitute(key: LessonKey, teacherId: string, version: number,
  confirm: boolean): Promise<ApiResult<Lesson>> {
  return apiPut<Lesson>(lessonPath(key) + "/substitute", { teacherId, version, confirm });
}

export async function changeRoom(key: LessonKey, classroomId: string, version: number,
  confirm: boolean): Promise<ApiResult<Lesson>> {
  return apiPut<Lesson>(lessonPath(key) + "/room", { classroomId, version, confirm });
}

export async function moveTime(key: LessonKey, range: CellRange, version: number,
  confirm: boolean): Promise<ApiResult<Lesson>> {
  return apiPut<Lesson>(lessonPath(key) + "/time", { range, version, confirm });
}

/** DELETE …/substitute | room | time: back to the plan. */
export async function resetLessonPart(key: LessonKey, part: "substitute" | "room" | "time", version: number,
  confirm: boolean): Promise<ApiResult<Lesson>> {
  return apiDelete<Lesson>(lessonPath(key) + `/${part}` + queryString({ version, confirm }));
}

export async function setLessonNote(key: LessonKey, note: string, version: number): Promise<ApiResult<Lesson>> {
  return apiPut<Lesson>(lessonPath(key) + "/note", { note, version });
}

export async function setTeacherNote(key: LessonKey, teacherNote: string,
  version: number): Promise<ApiResult<Lesson>> {
  return apiPut<Lesson>(lessonPath(key) + "/teacher-note", { teacherNote, version });
}
